import React, { useState, useEffect } from "react";
import FitNumber from "../../../common/FitNumber";
import { fetchData } from "../../../../utils/functions";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import NoData, { hasData } from "../../../common/NoData";
import DashboardBarChart from "../../../common/DashboardBarChart";

const EducationTab = ({ filters }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [data, setData] = useState({
    educationLevels: {
      total: 0,
      educationStats: {},
      educationByGender: { male: {}, female: {}, other: {} },
    },
    noEducationByGenderAge: {
      male: {
        "6-11": 0,
        "12-16": 0,
        "17-18": 0,
        "19-30": 0,
        "31-49": 0,
        "50+": 0,
      },
      female: {
        "6-11": 0,
        "12-16": 0,
        "17-18": 0,
        "19-30": 0,
        "31-49": 0,
        "50+": 0,
      },
    },
    children611Stats: {
      overall: 0,
      boys: 0,
      girls: 0,
      age6: 0,
      age7: 0,
      age8to11: 0,
    },
    schoolAttendance611: {
      attending: 0,
      notAttending: 0,
      total: 0,
    },
    reasonsNotAttending: [],
  });
  const [loading, setLoading] = useState(true);

  const formatCount = (num) =>
    num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") || "0";

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [
          educationRes,
          noEducationRes,
          children611Res,
          attendanceRes,
          reasonsRes,
        ] = await Promise.all([
          fetchData(
            "household-education-levels",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/household-education-levels",
            dispatch
          ),
          fetchData(
            "no-education-by-gender-age",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/no-education-by-gender-age",
            dispatch
          ),
          fetchData(
            "children-611-stats",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/children-611-stats",
            dispatch
          ),
          fetchData(
            "school-attendance-611",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/school-attendance-611",
            dispatch
          ),
          fetchData(
            "reasons-not-attending-school",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/reasons-not-attending-school",
            dispatch
          ),
        ]);

        setData({
          educationLevels: educationRes.data || {
            total: 0,
            educationStats: {},
            educationByGender: { male: {}, female: {}, other: {} },
          },
          noEducationByGenderAge: noEducationRes.data || {
            male: {
              "6-11": 0,
              "12-16": 0,
              "17-18": 0,
              "19-30": 0,
              "31-49": 0,
              "50+": 0,
            },
            female: {
              "6-11": 0,
              "12-16": 0,
              "17-18": 0,
              "19-30": 0,
              "31-49": 0,
              "50+": 0,
            },
          },
          children611Stats: children611Res.data || {
            overall: 0,
            boys: 0,
            girls: 0,
            age6: 0,
            age7: 0,
            age8to11: 0,
          },
          schoolAttendance611: attendanceRes.data || {
            attending: 0,
            notAttending: 0,
            total: 0,
          },
          reasonsNotAttending: reasonsRes.data || [],
        });
      } catch (err) {
        console.error("Error loading Education tab:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [filters, navigate, dispatch]);

  // Education Levels by Gender Bar Chart
  const educationCategories = [
    "Pre-school/Katchi",
    "Primary",
    "Lower Secondary",
    "Upper Secondary",
    "Higher Secondary",
    "Graduate/Above",
    "None",
  ];

  const educationLevelsSeries = [
    {
      name: "Male",
      data: [
        data.educationLevels.educationByGender.male["Pre-school/Katchi"] || 0,
        data.educationLevels.educationByGender.male["Primary (five years)"] ||
          0,
        data.educationLevels.educationByGender.male["Lower Secondary"] || 0,
        data.educationLevels.educationByGender.male["Upper Secondary"] || 0,
        data.educationLevels.educationByGender.male["Higher Secondary"] || 0,
        data.educationLevels.educationByGender.male["Graduate/Above"] || 0,
        data.educationLevels.educationByGender.male["None"] || 0,
      ],
    },
    {
      name: "Female",
      data: [
        data.educationLevels.educationByGender.female["Pre-school/Katchi"] || 0,
        data.educationLevels.educationByGender.female["Primary (five years)"] ||
          0,
        data.educationLevels.educationByGender.female["Lower Secondary"] || 0,
        data.educationLevels.educationByGender.female["Upper Secondary"] || 0,
        data.educationLevels.educationByGender.female["Higher Secondary"] || 0,
        data.educationLevels.educationByGender.female["Graduate/Above"] || 0,
        data.educationLevels.educationByGender.female["None"] || 0,
      ],
    },
  ];

  // No Education by Gender and Age Bar Chart
  const noEducationCategories = ["6-11", "12-16", "17-18", "19-30", "31-49", "50+"];

  const noEducationSeries = [
    {
      name: "Male",
      data: Object.values(data.noEducationByGenderAge.male),
    },
    {
      name: "Female",
      data: Object.values(data.noEducationByGenderAge.female),
    },
  ];

  const allReasons = [
    "Far away",
    "Costly",
    "Lack of male/female teachers",
    "Quality of teaching",
    "School building",
    "Lack of boundary wall",
    "Water and sanitation facilities",
    "Child labor",
    "Others",
    "Enrolled in Madarsah",
  ];

  // Create a map of existing data
  const reasonsDataMap = {};
  data.reasonsNotAttending.forEach((item) => {
    reasonsDataMap[item.reason] = item.count;
  });

  // Build complete data with all reasons
  const completeReasonsData = allReasons.map((reason) => ({
    reason: reason,
    count: reasonsDataMap[reason] || 0,
  }));

  const totalNotAttending = completeReasonsData.reduce(
    (sum, item) => sum + item.count,
    0
  );

  const toShare = (count) =>
    data.schoolAttendance611.total > 0
      ? ((count / data.schoolAttendance611.total) * 100).toFixed(1)
      : "0.0";

  const has = {
    children: hasData(data.children611Stats),
    educationLevels: hasData(educationLevelsSeries.map((s) => s.data)),
    attendance: hasData(
      data.schoolAttendance611.attending,
      data.schoolAttendance611.notAttending
    ),
    noEducation: hasData(data.noEducationByGenderAge),
    reasons: hasData(totalNotAttending),
  };

  const anyData = Object.values(has).some(Boolean);
  const showRow = () => loading || anyData;
  const panel = (hasPanelData, body) =>
    !loading && !hasPanelData ? <NoData /> : body;

  return (
    <div className="space-y-6 pb-8">
      {!loading && !Object.values(has).some(Boolean) && (
        <NoData className="py-16" />
      )}
      {/* Row 1: Children (6-11) Statistics */}
      {showRow(has.children) && (
      <div className="bg-white flex flex-col h-full">
        <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
          Children (6-11 Years) Statistics
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-6 p-2 gap-4">
          <div className="bg-[#E6F2F2] p-4 text-center border-l-4 border-[#137E81]">
            <p className="text-2xl font-extrabold text-[#137E81]">
              <FitNumber value={data.children611Stats.overall} />
            </p>
            <p className="text-xs text-[#0B5557] mt-2 font-semibold">Overall</p>
          </div>
          <div className="bg-[#E6F2F2] p-4 text-center border-l-4 border-[#137E81]">
            <p className="text-2xl font-extrabold text-[#137E81]">
              <FitNumber value={data.children611Stats.boys} />
            </p>
            <p className="text-xs text-[#0B5557] mt-2 font-semibold">Boys</p>
            <p className="text-xs text-[#0B5557] mt-1">
              {data.children611Stats.overall > 0
                ? (
                    (data.children611Stats.boys /
                      data.children611Stats.overall) *
                    100
                  ).toFixed(1)
                : 0}
              %
            </p>
          </div>
          <div className="bg-[#E6F2F2] p-4 text-center border-l-4 border-[#137E81]">
            <p className="text-2xl font-extrabold text-[#137E81]">
              <FitNumber value={data.children611Stats.girls} />
            </p>
            <p className="text-xs text-[#0B5557] mt-2 font-semibold">Girls</p>
            <p className="text-xs text-[#0B5557] mt-1">
              {data.children611Stats.overall > 0
                ? (
                    (data.children611Stats.girls /
                      data.children611Stats.overall) *
                    100
                  ).toFixed(1)
                : 0}
              %
            </p>
          </div>
          <div className="bg-[#E6F2F2] p-4 text-center border-l-4 border-[#137E81]">
            <p className="text-2xl font-extrabold text-[#137E81]">
              <FitNumber value={data.children611Stats.age6} />
            </p>
            <p className="text-xs text-[#0B5557] mt-2 font-semibold">Age 6</p>
            <p className="text-xs text-[#0B5557] mt-1">
              {data.children611Stats.overall > 0
                ? (
                    (data.children611Stats.age6 /
                      data.children611Stats.overall) *
                    100
                  ).toFixed(1)
                : 0}
              %
            </p>
          </div>
          <div className="bg-[#E6F2F2] p-4 text-center border-l-4 border-[#137E81]">
            <p className="text-2xl font-extrabold text-[#137E81]">
              <FitNumber value={data.children611Stats.age7} />
            </p>
            <p className="text-xs text-[#0B5557] mt-2 font-semibold">Age 7</p>
            <p className="text-xs text-[#0B5557] mt-1">
              {data.children611Stats.overall > 0
                ? (
                    (data.children611Stats.age7 /
                      data.children611Stats.overall) *
                    100
                  ).toFixed(1)
                : 0}
              %
            </p>
          </div>
          <div className="bg-[#E6F2F2] p-4 text-center border-l-4 border-[#137E81]">
            <p className="text-2xl font-extrabold text-[#137E81]">
              <FitNumber value={data.children611Stats.age8to11} />
            </p>
            <p className="text-xs text-[#0B5557] mt-2 font-semibold">Age 8-11</p>
            <p className="text-xs text-[#0B5557] mt-1">
              {data.children611Stats.overall > 0
                ? (
                    (data.children611Stats.age8to11 /
                      data.children611Stats.overall) *
                    100
                  ).toFixed(1)
                : 0}
              %
            </p>
          </div>
        </div>
      </div>
      )}

      {/* Row 2: Education Levels, School Attendance & No Education - 3 columns */}
      {showRow(has.educationLevels, has.attendance, has.noEducation) && (<>
      <hr className="border-t-2 border-dashed border-teal-700 first:hidden" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Household Education Levels by Gender */}
        <div className="bg-white flex flex-col h-full">
          <h3 className="text-lg text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Education Levels by Gender
          </h3>
          <p className="text-center text-xs text-gray-600 mb-2">
            (Members &gt; 5 years)
          </p>
          {panel(has.educationLevels,
          <DashboardBarChart
            vertical
            stacked={false}
            tilt
            categories={educationCategories}
            series={educationLevelsSeries}
            unit="members"
          />)}
        </div>

        {/* School Attendance (Children Age 6-11) */}
        <div className="bg-white flex flex-col h-full">
          <h3 className="text-lg text-center text-[#3A3A3A] font-bold uppercase mb-4 text-balance">
            School Attendance (Age 6-11)
          </h3>

          {panel(has.attendance,
          <DashboardBarChart
            items={[
              {
                name: "Attending",
                count: data.schoolAttendance611.attending,
                percentage: toShare(data.schoolAttendance611.attending),
              },
              {
                name: "Not Attending",
                count: data.schoolAttendance611.notAttending,
                percentage: toShare(data.schoolAttendance611.notAttending),
              },
            ]}
            unit="children"
          />)}
        </div>

        {/* No Education by Gender and Age Groups */}
        <div className="bg-white flex flex-col h-full">
          <h3 className="text-lg text-center text-[#3A3A3A] font-bold uppercase text-balance">
            No Education by Gender & Age
          </h3>
          {panel(has.noEducation,
          <DashboardBarChart
            vertical
            stacked={false}
            categories={noEducationCategories}
            series={noEducationSeries}
            unit="persons"
          />)}
        </div>
      </div>
      </>)}

      {/* Row 3: Reasons for Not Attending School */}
      {showRow(has.reasons) && (<>
      <hr className="border-t-2 border-dashed border-teal-700 first:hidden" />
      <div className="bg-white flex flex-col h-full">
        <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
          Reasons for Not Attending School
        </h3>
        <div className="mb-4 text-center">
          <span className="text-sm font-semibold text-[#3A3A3A]">
            Total Children Not Attending: {formatCount(totalNotAttending)}
          </span>
        </div>
        <DashboardBarChart horizontal
          items={completeReasonsData.map((item) => ({
            name: item.reason,
            count: item.count,
          }))}
          unit="children"
        />
      </div>
      </>)}
    </div>
  );
};

export default EducationTab;
